"use client";

import { useEffect, useRef } from "react";
import { reduceMotion } from "@/lib/motion";

/** Brand teals plus ink and white, so the burst reads against the card and the backdrop. */
const COLORS = ["#2de2d0", "#8befe5", "#12796f", "#0a0a0b", "#ffffff"];

const GRAVITY = 0.32;
const DRAG = 0.992;
const PER_VOLLEY = 70;
const LIFE = 150; // frames, ~2.5s

type Piece = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  rot: number;
  vr: number;
  color: string;
  life: number;
};

/**
 * A party-popper burst: two angled volleys from the lower corners, then gravity.
 *
 * Drawn on a canvas rather than as elements — 140 absolutely-positioned nodes animating
 * at once would cost far more than one 2D context, and none of it is content.
 */
export function Confetti() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || reduceMotion()) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const rand = (a: number, b: number) => a + Math.random() * (b - a);
    const pieces: Piece[] = [];

    /** One popper: `angle` is the centre of its cone, in radians, y pointing up. */
    const volley = (originX: number, originY: number, angle: number) => {
      for (let i = 0; i < PER_VOLLEY; i++) {
        const a = angle + rand(-0.42, 0.42);
        const speed = rand(11, 23);
        pieces.push({
          x: originX,
          y: originY,
          vx: Math.cos(a) * speed,
          vy: -Math.sin(a) * speed,
          w: rand(5, 10),
          h: rand(7, 14),
          rot: rand(0, Math.PI * 2),
          vr: rand(-0.3, 0.3),
          color: COLORS[(Math.random() * COLORS.length) | 0],
          life: LIFE + rand(-30, 30),
        });
      }
    };

    volley(w * 0.06, h * 0.94, Math.PI / 3.1); // lower-left, firing up and right
    volley(w * 0.94, h * 0.94, Math.PI - Math.PI / 3.1); // lower-right, mirrored

    let frame = 0;
    let raf = 0;
    const tick = () => {
      frame++;
      ctx.clearRect(0, 0, w, h);
      let alive = 0;

      for (const p of pieces) {
        if (p.life <= 0) continue;
        alive++;
        p.life--;
        p.vx *= DRAG;
        p.vy = p.vy * DRAG + GRAVITY;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        // Fade over the last third of a piece's life, and flatten it as it tumbles.
        ctx.globalAlpha = Math.min(1, p.life / (LIFE * 0.34));
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * Math.abs(Math.cos(frame * 0.08 + p.rot)));
        ctx.restore();
      }

      if (alive) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} aria-hidden="true" className="pointer-events-none fixed inset-0 z-[70]" />;
}
